import { Duration, formatDuration, intervalToDuration, parseISO } from 'date-fns'
import { FinanceValueObjectsDealStageEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

// TODO this is copied from factorial-one but currently they're not exporting it
type Variant = 'neutral' | 'info' | 'positive' | 'warning' | 'critical'

export const dealTranslateStatus = (status: FinanceValueObjectsDealStageEnum): string => {
  switch (status) {
    case 'new':
      return i18n.t('finance.deals.status.new')
    case 'qualification':
      return i18n.t('finance.deals.status.qualification')
    case 'proposal_sent':
      return i18n.t('finance.deals.status.proposal_sent')
    case 'negotiation':
      return i18n.t('finance.deals.status.negotiation')
    case 'closed_won':
      return i18n.t('finance.deals.status.closed_won')
    case 'closed_lost':
      return i18n.t('finance.deals.status.closed_lost')
    default:
      return status satisfies never
  }
}

export const dealStatusAsVariant = (status: FinanceValueObjectsDealStageEnum): Variant => {
  switch (status) {
    case 'new':
      return 'neutral'
    case 'qualification':
      return 'info'
    case 'proposal_sent':
      return 'positive'
    case 'negotiation':
      return 'warning'
    case 'closed_won':
      return 'positive'
    case 'closed_lost':
      return 'critical'
    default:
      return status satisfies never
  }
}

export const dealStageAsValue = (status: string): FinanceValueObjectsDealStageEnum => {
  switch (status) {
    case 'new':
      return 'new'
    case 'qualification':
      return 'qualification'
    case 'proposal_sent':
      return 'proposal_sent'
    case 'negotiation':
      return 'negotiation'
    case 'closed_won':
      return 'closed_won'
    case 'closed_lost':
      return 'closed_lost'
    default:
      throw new Error(`Invalid deal stage: ${status}`)
  }
}

type DealStageOption = {
  value: FinanceValueObjectsDealStageEnum
  label: string
}

export const getDealStageOptions = (): DealStageOption[] => [
  { value: 'new', label: i18n.t('finance.deals.status.new') },
  { value: 'qualification', label: i18n.t('finance.deals.status.qualification') },
  { value: 'proposal_sent', label: i18n.t('finance.deals.status.proposal_sent') },
  { value: 'negotiation', label: i18n.t('finance.deals.status.negotiation') },
  { value: 'closed_won', label: i18n.t('finance.deals.status.closed_won') },
  { value: 'closed_lost', label: i18n.t('finance.deals.status.closed_lost') },
]

type DealLike = {
  stage: FinanceValueObjectsDealStageEnum
  createdAt: string
  qualificationAt?: string | null
  proposalSentAt?: string | null
  negotiationAt?: string | null
  closedWonAt?: string | null
  closedLostAt?: string | null
}

export type DealProgress = {
  stageIndex: number // 1..5
  progressColor: string // 'info-50'
  totalTimeInStageFormatted: string
  totalTimeInPipelineFormatted: string
}

export const computeDealProgress = (deal: DealLike, now: Date = new Date()): DealProgress => {
  const stageIndex = (() => {
    switch (deal.stage) {
      case 'new':
        return 1
      case 'qualification':
        return 2
      case 'proposal_sent':
        return 3
      case 'negotiation':
        return 4
      case 'closed_won':
      case 'closed_lost':
        return 5
      default:
        return 1
    }
  })()

  const parseDate = (value?: string | null): Date | undefined => {
    if (!value) return undefined
    const parsed = parseISO(value)
    return isNaN(parsed.getTime()) ? undefined : parsed
  }

  const created = parseISO(deal.createdAt)

  const stageStartDate = (() => {
    switch (deal.stage) {
      case 'new':
        return created
      case 'qualification': {
        return parseDate(deal.qualificationAt) || created
      }
      case 'proposal_sent': {
        return parseDate(deal.proposalSentAt) || parseDate(deal.qualificationAt) || created
      }
      case 'negotiation': {
        return (
          parseDate(deal.negotiationAt) ||
          parseDate(deal.proposalSentAt) ||
          parseDate(deal.qualificationAt) ||
          created
        )
      }
      case 'closed_won': {
        return (
          parseDate(deal.closedWonAt) ||
          parseDate(deal.negotiationAt) ||
          parseDate(deal.proposalSentAt) ||
          parseDate(deal.qualificationAt) ||
          created
        )
      }
      case 'closed_lost': {
        return (
          parseDate(deal.closedLostAt) ||
          parseDate(deal.negotiationAt) ||
          parseDate(deal.proposalSentAt) ||
          parseDate(deal.qualificationAt) ||
          created
        )
      }
      default:
        return created
    }
  })()

  const isClosed = deal.stage === 'closed_won' || deal.stage === 'closed_lost'
  const closedAt = parseDate(deal.closedWonAt) || parseDate(deal.closedLostAt)
  const endForStage = isClosed && closedAt ? closedAt : now
  const endForPipeline = isClosed && closedAt ? closedAt : now

  const totalTimeInStageFormatted = formatDateDistance(endForStage, stageStartDate)
  const totalTimeInPipelineFormatted = formatDateDistance(endForPipeline, created)

  const progressColor = `${dealStatusAsVariant(deal.stage)}-50`

  return {
    stageIndex,
    progressColor,
    totalTimeInStageFormatted,
    totalTimeInPipelineFormatted,
  }
}

function formatDateDistance(
  endDate: Date,
  startDate: Date,
  units: (keyof Duration)[] = ['years', 'months', 'days', 'hours', 'minutes']
) {
  let duration = intervalToDuration({ start: startDate, end: endDate })

  // filter the units with a value different from 0 to not show '0 months'
  let filteredUnits = units.filter((unit) => duration?.[unit] && duration[unit] > 0)

  // if all of the units are 0 or only has seconds, we show '1 minute'
  if (filteredUnits.length === 0) {
    filteredUnits = ['minutes']
    duration = { minutes: 1 }
  }

  return formatDuration(duration, {
    format: filteredUnits,
  })
}
